<template>
    <div class="flex items-center justify-between mb-8">
        <Head :title="$t('taxRates.title')" />
        <page-title :title="$t('taxRates.title')"></page-title>

        <div class="flex md:mt-0 md:ml-4">
            <primary-button
                type="button"
                class="ml-3 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium text-white bg-minplan-greybeige hover:bg-minplan-lightgreybeige focus:outline-none uppercase"
                @click="toCreatePage()"
            >
                <PlusSmIcon
                    class="-ml-1 md:mr-2 h-5 w-5 text-white md:text-minplan-darkgreybeige"
                />
                <span class="hidden md:inline font-bold">{{
                    $t("taxRates.newRate")
                }}</span>
            </primary-button>
        </div>
    </div>

    <div class="bg-red-100 border border-red-100 p-3 rounded my-6">
        <h2 class="font-semibold text-lg mb-2 text-red-800 flex items-center">
            <ExclamationCircleIcon class="-ml-1 md:mr-2 h-5 w-5 text-red-600" />
            {{ $t("taxRates.importantNotice") }}
        </h2>
        <p class="text-sm leading-relaxed text-red-900">
            {{ $t("taxRates.info") }}
        </p>
    </div>

    <!-- TAX RATES -->
    <ul role="list" class="mt-6">
        <li v-for="rate in taxRates" :key="rate.uuid">
            <tax-rate-card
                :rate="rate"
                :on-delete="showDeleteModal"
                :on-make-default="makeDefault"
            ></tax-rate-card>
        </li>
    </ul>

    <jet-confirmation-modal :show="showDeleteConfirmation">
        <template #title>{{ $t("taxRates.deleteRate") }}</template>

        <template #content>{{ $t("taxRates.deleteRateInfo") }}</template>

        <template #footer>
            <div class="flex justify-end mx-6">
                <button
                    @click="cancelDeleteModal"
                    type="button"
                    class="inline-block uppercase font-semibold text-xs tracking-widest"
                >
                    {{ $t("actions.cancel") }}
                </button>
                <jet-danger-button class="ml-2" @click="deleteRate()">
                    {{ $t("actions.delete") }}
                </jet-danger-button>
            </div>
        </template>
    </jet-confirmation-modal>
</template>

<script>
import { defineComponent, ref } from "vue";
import MainLayout from "@/Layouts/MainLayout";
import SettingsLayout from "@/Layouts/SettingsLayout";
import { ExclamationCircleIcon, PlusSmIcon, SearchIcon } from "@heroicons/vue/outline";
import Pagination from "@/Shared/Components/Pagination";
import PageTitle from "@/Shared/Components/PageTitle";
import TemplateCard from "@/Pages/Templates/Components/TemplateCard";
import PrimaryButton from "@/Jetstream/PrimaryButton.vue";
import JetConfirmationModal from "@/Jetstream/ConfirmationModal.vue";
import TaxRateCard from "@/Pages/TaxRates/Components/TaxRateCard";
import JetDangerButton from "@/Jetstream/DangerButton.vue";
import { Head } from "@inertiajs/inertia-vue3";

export default defineComponent({
    props: ["taxRates"],
    layout: [MainLayout, SettingsLayout],
    components: {
        Head,
        JetConfirmationModal,
        PrimaryButton,
        PlusSmIcon,
        SearchIcon,
        Pagination,
        PageTitle,
        TemplateCard,
        ExclamationCircleIcon,
        TaxRateCard,
        JetDangerButton,
    },
    setup() {
        const showDeleteConfirmation = ref(false);
        const currentDeletableRate = ref(null);

        return {
            showDeleteConfirmation,
            currentDeletableRate,
        };
    },
    methods: {
        toCreatePage() {
            this.$inertia.visitInModal(this.route("advisors.taxRates.create"));
        },
        makeDefault(rate) {
            this.$inertia.put(
                this.route("advisors.taxRates.makeDefault", { taxRate: rate }),
                {
                    preserveScroll: true,
                    onSuccess: () =>
                        this.$notify({
                            group: "success",
                            title: "actions.success",
                            text: "taxRates.madeDefault",
                        }),
                }
            );
        },
        showDeleteModal(rateId) {
            this.showDeleteConfirmation = true;
            this.currentDeletableRate = rateId;
        },
        cancelDeleteModal() {
            this.showDeleteConfirmation = false;
            this.currentDeletableRate = null;
        },
        deleteRate() {
            this.$inertia.delete(
                this.route("advisors.taxRates.destroy", {
                    taxRate: this.currentDeletableRate,
                }),
                {
                    preserveScroll: true,
                    onSuccess: () => {
                        this.$notify({
                            group: "success",
                            title: "actions.success",
                            text: "taxRates.deleted",
                        });
                        this.cancelDeleteModal();
                    },
                }
            );
        },
    },
});
</script>

<style scoped></style>
